import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Shield, Zap, Globe } from 'lucide-react';

const Hero = () => {
  const scrollToWaitlist = () => {
    document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-emerald-50/30 pt-20">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#40E095]/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#40E095]/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left space-y-8">
            <div className="inline-flex items-center space-x-2 bg-[#40E095]/10 text-[#40E095] px-4 py-2 rounded-full text-sm font-medium">
              <Shield size={16} />
              <span>Invite-Only Access • 100GB Free</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Your Files.{' '}
              <span className="text-[#40E095]">Everywhere.</span>{' '}
              <span className="block mt-2">Effortlessly.</span>
            </h1>

            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0">
              Secure, cross-platform cloud storage built for trusted privacy. 
              Access your files seamlessly across Windows, macOS, iOS, Android, and Linux.
            </p>

            {/* Key Stats */}
            <div className="flex flex-wrap gap-6 justify-center lg:justify-start">
              <div className="flex items-center space-x-2">
                <Shield className="text-[#40E095]" size={20} />
                <span className="text-gray-700 font-medium">128-bit Encryption</span>
              </div>
              <div className="flex items-center space-x-2">
                <Zap className="text-[#40E095]" size={20} />
                <span className="text-gray-700 font-medium">Lightning Fast</span>
              </div>
              <div className="flex items-center space-x-2">
                <Globe className="text-[#40E095]" size={20} />
                <span className="text-gray-700 font-medium">5 Platforms</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button 
                onClick={scrollToWaitlist}
                size="lg"
                className="bg-[#40E095] hover:bg-[#36C981] text-white font-semibold px-8 py-6 text-lg transition-all hover:shadow-xl hover:scale-105 group"
              >
                Request Your Invite
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
              <Button 
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                variant="outline"
                className="border-2 border-[#40E095] text-[#40E095] hover:bg-[#40E095] hover:text-white font-semibold px-8 py-6 text-lg transition-all"
              >
                Learn More
              </Button>
            </div>

            {/* Trust Badge */}
            <p className="text-sm text-gray-500 flex items-center justify-center lg:justify-start space-x-2">
              <Shield size={16} className="text-[#40E095]" />
              <span>Trusted by 10,000+ users • No credit card required</span>
            </p>
          </div>

          {/* Right Content - Hero Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
              <img 
                src="https://images.unsplash.com/photo-1625645240391-fb79e10823a2" 
                alt="Cloud Storage Across Devices" 
                className="w-full h-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#40E095]/20 to-transparent"></div>
            </div>

            {/* Floating Cards */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 animate-float">
              <div className="flex items-center space-x-3">
                <div className="bg-[#40E095]/10 p-3 rounded-lg">
                  <Shield className="text-[#40E095]" size={24} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">100% Secure</p>
                  <p className="text-xs text-gray-600">End-to-end encrypted</p>
                </div>
              </div>
            </div>

            <div className="absolute -top-6 -right-6 bg-white rounded-xl shadow-xl p-4 animate-float" style={{ animationDelay: '1s' }}>
              <div className="flex items-center space-x-3">
                <div className="bg-[#40E095]/10 p-3 rounded-lg">
                  <Zap className="text-[#40E095]" size={24} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Ultra Fast</p>
                  <p className="text-xs text-gray-600">Instant sync</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default Hero;
